import React, { useState } from 'react';
import { apiClient } from '../../../lib/api';
import { Search } from 'lucide-react';

interface UserDetails {
  user_id: string;
  profile: {
    company_website?: string;
    onboarding_completed: boolean;
    role?: string;
    primary_goal?: string;
    referral_source?: string;
    created_at: string;
  };
  auth: {
    email?: string;
    email_confirmed_at?: string;
    created_at?: string;
    last_sign_in_at?: string;
  };
  datasets: {
    total: number;
    by_status: Record<string, number>;
  };
  emails: {
    total_sent: number;
  };
  credits: {
    total_credited: number;
    balance: number;
    transactions_count: number;
  };
}

export const UserDetailsTab: React.FC = () => {
  const [userIdInput, setUserIdInput] = useState('');
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFetchUserDetails = async () => {
    if (!userIdInput.trim()) {
      setError('Please enter a user ID');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getUserDetails(userIdInput.trim());
      setUserDetails(data);
    } catch (err: unknown) {
      console.error('Error fetching user details:', err);
      const error = err as { response?: { data?: { detail?: string } } };
      setError(error.response?.data?.detail || 'Failed to load user details');
      setUserDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleFetchUserDetails();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">User Details</h2>
        <p className="text-sm text-gray-600 mt-1">View detailed information for a specific user</p>
      </div>

      {/* Search Input */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          User ID
        </label>
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Enter user UUID"
              value={userIdInput}
              onChange={(e) => setUserIdInput(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
          <button
            onClick={handleFetchUserDetails}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : 'Search'}
          </button>
        </div>
        {error && (
          <p className="mt-2 text-sm text-red-600">{error}</p>
        )}
      </div>

      {/* User Details Display */}
      {userDetails && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Profile Card */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Profile Information</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">User ID</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">{userDetails.user_id}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Email</dt>
                <dd className="mt-1 text-sm text-gray-900">{userDetails.auth.email || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Company Website</dt>
                <dd className="mt-1 text-sm text-gray-900">{userDetails.profile.company_website || 'Not provided'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Role</dt>
                <dd className="mt-1 text-sm text-gray-900">{userDetails.profile.role || 'Not set'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Primary Goal</dt>
                <dd className="mt-1 text-sm text-gray-900">{userDetails.profile.primary_goal || 'Not set'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Referral Source</dt>
                <dd className="mt-1 text-sm text-gray-900">{userDetails.profile.referral_source || 'Not set'}</dd>
              </div>
            </dl>
          </div>

          {/* Auth Status Card */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Authentication Status</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Email Confirmed</dt>
                <dd className="mt-1">
                  {userDetails.auth.email_confirmed_at ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ✓ Confirmed
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Pending
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Onboarding Complete</dt>
                <dd className="mt-1">
                  {userDetails.profile.onboarding_completed ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ✓ Complete
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Incomplete
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Account Created</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {userDetails.auth.created_at ? new Date(userDetails.auth.created_at).toLocaleString() : 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Last Sign In</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {userDetails.auth.last_sign_in_at ? new Date(userDetails.auth.last_sign_in_at).toLocaleString() : 'Never'}
                </dd>
              </div>
            </dl>
          </div>

          {/* Activity Stats Card */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Activity Statistics</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-500">Datasets Created</span>
                <span className="text-2xl font-bold text-purple-600">{userDetails.datasets.total}</span>
              </div>
              {Object.keys(userDetails.datasets.by_status).length > 0 && (
                <div className="pl-4 space-y-2">
                  {Object.entries(userDetails.datasets.by_status).map(([status, count]) => (
                    <div key={status} className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 capitalize">{status.replace('_', ' ')}</span>
                      <span className="text-gray-900 font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-between items-center border-t pt-4">
                <span className="text-sm font-medium text-gray-500">Emails Sent</span>
                <span className="text-2xl font-bold text-green-600">{userDetails.emails.total_sent}</span>
              </div>
            </div>
          </div>

          {/* Credits Card */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Credits & Billing</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-500">Total Credited</span>
                <span className="text-2xl font-bold text-blue-600">{userDetails.credits.total_credited.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-500">Current Balance</span>
                <span className="text-2xl font-bold text-orange-600">{userDetails.credits.balance.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center border-t pt-4">
                <span className="text-sm font-medium text-gray-500">Transactions</span>
                <span className="text-lg font-medium text-gray-900">{userDetails.credits.transactions_count}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!userDetails && !error && (
        <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <Search className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No user selected</h3>
          <p className="mt-1 text-sm text-gray-500">
            Enter a user ID above to view their detailed information
          </p>
        </div>
      )}
    </div>
  );
};
